import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import cn from 'classnames'

import { IMessengerChat, IUser, IUserData } from 'interfaces'
import { APP_URL } from 'config/app'
import { MessengerItemInner, MessengerMenuConversation } from 'containers/Messenger/components'
import { useMessenger } from 'containers/Messenger/hooks'
import { Avatar, AvatarStack, Link } from 'components'
import { numberFormat, plural } from 'utils/helpers'
import style from './MessengerConversationChat.module.css'

type ChatItemPropType = {
    classes?: string
    data: IMessengerChat
    user: IUser
    chatUserData?: IUserData
    onMarkRead: (id: number) => void
    onUnMarkRead: (id: number) => void
}

const COUNT_FRIENDS = 3

const MessengerConversationChat: React.FC<ChatItemPropType> = ({
    classes,
    data,
    user,
    chatUserData,
    onMarkRead,
    onUnMarkRead,
}) => {
    const { t } = useTranslation()

    const { isReceivedLastMessage, isReadLastMessage } = useMessenger()

    const [chatUser] = useState(user.id === data.starterUserId ? data.followerUser : data.starterUser)

    const isReceived = useMemo(() => {
        return isReceivedLastMessage(user.id, data.lastMessage)
    }, [data])

    const isRead = useMemo(() => {
        return isReadLastMessage(user.id, data.lastMessage)
    }, [data])

    const urlChat = useMemo(() => {
        return APP_URL.messengerChat.replace(':id', String(chatUser.id))
    }, [data])

    const urlMutualFriends = useMemo(() => {
        return chatUserData ? APP_URL.friendsMutual.replace(':id', String(chatUserData.user.id)) : ''
    }, [chatUserData])

    const mutualFriends = useMemo(() => {
        if (chatUserData?.friends) {
            return chatUserData.friends.map((item) => ({
                id: item.id,
                name: item.name ?? '',
                surname: item.surname ?? '',
                photo: item.photo ?? '',
            }))
        }
        return []
    }, [chatUserData])

    const mutualFriendsCountMore = useMemo(() => {
        return !!chatUserData?.total && chatUserData.total > COUNT_FRIENDS ? chatUserData.total - COUNT_FRIENDS : 0
    }, [chatUserData])

    const mutualFriendsText = useMemo(() => {
        if (mutualFriendsCountMore) {
            const textPart1 = plural(mutualFriendsCountMore, [t('mutual_1x'), t('mutual_2x'), t('mutual_5x')])
            const textPart2 = plural(mutualFriendsCountMore, [t('friend_1x'), t('friend_2x'), t('friend_5x')])

            return `+${numberFormat(mutualFriendsCountMore)} ${textPart1} ${textPart2}`
        }
        return ''
    }, [mutualFriendsCountMore])

    const handlerMarkRead = () => {
        onMarkRead(chatUser.id)
    }

    const handlerMarkUnRead = () => {
        onUnMarkRead(chatUser.id)
    }

    const avatarTpl = (
        <Link className={style.user} url={urlChat}>
            <Avatar
                name={chatUser.name}
                surname={chatUser.surname}
                src={chatUser.photoFullUrl}
                width={48}
                height={48}
            />
        </Link>
    )

    const contentTpl = (
        <Link className={style.friends} url={urlMutualFriends}>
            <AvatarStack
                isKeepHeight={false}
                users={mutualFriends}
                count={3}
                size={28}
            />
            <div className={cn(style.friendsText, { [style.friendsText_mLeft]: !!mutualFriendsText })}>
                {mutualFriendsText && mutualFriendsText}
            </div>
        </Link>
    )

    const menuTpl = (
        <MessengerMenuConversation
            onMarkRead={data.unreadMessagesCount ? handlerMarkRead : undefined}
            onMarkUnRead={!data.unreadMessagesCount ? handlerMarkUnRead : undefined}
        />
    )

    return (
        <MessengerItemInner
            classes={classes}
            title={`${chatUser.name ?? ''} ${chatUser.surname ?? ''}`}
            avatar={avatarTpl}
            content={contentTpl}
            menu={menuTpl}
            message={data.lastMessage ? (data.lastMessage.text || data.lastMessage.forwardedMessage) : ''}
            messageLines={2}
            // FIXME in KW-184 KW-185
            // countNew={data.unreadMessagesCount}
            date={data.lastMessage?.createdAt || data.createdAt}
            url={urlChat}
            isSimpleMessage
            isMarkedUnread={data.isMarkedAsUnreadByStarter || data.isMarkedAsUnreadByFollower}
            isReceived={isReceived}
            isRead={isRead}
            isError={false}
        />
    )
}

export default MessengerConversationChat
